// src/pages/ConsentPage.jsx
// Consent gate page - users must accept consent before accessing Home
// This is a legal requirement and cannot be bypassed

import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ConsentContext } from '../context/ConsentContext';
import { UserContext } from '../context/UserContext';
import api from '../utils/api';
import '../assets/styles/ConsentPage.css';

const ConsentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { authToken } = useContext(UserContext) || {};
  const { consent, loading: consentLoading, submitConsent, acceptConsent } = useContext(ConsentContext) || {};
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);
  const [checking, setChecking] = useState(true);

  const isAuthenticated = Boolean(authToken);
  // Read nextPath from router state, default to '/'
  const nextPath = location?.state?.next || '/';

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // On mount: check if user already has consent
  useEffect(() => {
    const checkExistingConsent = async () => {
      if (!isAuthenticated || consentLoading) return;

      try {
        const res = await api.get('/api/consent/current/', { timeout: 1500 });
        const data = res.data || {};
        
        if (data.has_consent === true && data.consent_required !== true) {
          // User already has consent - hydrate cache and redirect
          localStorage.setItem('cookie_consent_given', 'true');
          localStorage.setItem('consent_cached', JSON.stringify({ ...data, has_consent: true, consent_required: false }));
          navigate(nextPath, { replace: true });
        }
      } catch (err) {
        // Error checking - stay on consent page
        console.error('consent error', err?.response?.status, err?.response?.data || err?.message);
      } finally {
        setChecking(false);
      }
    };

    checkExistingConsent();
  }, [isAuthenticated, consentLoading, navigate, nextPath]);

  const handleAccept = async () => {
    setAccepting(true);
    setError(null);

    try {
      // Step 1: Submit full consent (this is the real persistence)
      const result = await submitConsent({
        consents: {
          essential: true,
          treatment: true,
          analytics: true,
          communications: true,
          research: true,
          providerSharing: true,
          cookies: true,
        },
        timestamp: new Date().toISOString(),
        appVersion: process.env.REACT_APP_VERSION || '1.0.0',
        version: '2025.1',
      });

      // Step 2: Persist cache flags
      localStorage.setItem('cookie_consent_given', 'true');
      if (result) {
        localStorage.setItem('consent_cached', JSON.stringify({ ...result, has_consent: true, consent_required: false }));
      }

      // Step 3: Redirect immediately (don't wait for acceptConsent)
      navigate(nextPath, { replace: true });

      // Step 4: Optional best-effort acceptConsent in background (non-blocking)
      acceptConsent().catch(() => {
        // Ignore errors - submitConsent already succeeded
      });
    } catch (err) {
      // Extract error message from backend response
      const errorMessage = err?.response?.data?.detail || 
                          err?.response?.data?.error || 
                          err?.response?.data?.message ||
                          'Failed to accept consent. Please try again.';
      setError(errorMessage);
      console.error('consent error', err?.response?.status, err?.response?.data || err?.message);
    } finally {
      setAccepting(false);
    }
  };

  const handleLogout = () => {
    // Clear auth token and redirect to login
    localStorage.removeItem('authToken');
    navigate('/login', { replace: true });
  };

  if (!isAuthenticated) {
    return null; // Will redirect
  }

  // Only show spinner if checking existing consent, not during normal loading
  if (checking || consentLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #10B981',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="consent-page">
      <div className="consent-container">
        <div className="consent-header">
          <h1>BloomPal Data & Wellness Consent</h1>
          <p className="consent-subtitle">
            Before you can access your Home page, we need your consent to process your wellness data.
          </p>
        </div>

        <div className="consent-content">
          <div className="consent-section">
            <h2>What We Do With Your Data</h2>
            <ul>
              <li>
                <strong>Essential Services:</strong> We use your data to provide personalized wellness activities, 
                mood tracking, and recommendations tailored to your needs.
              </li>
              <li>
                <strong>Treatment Support:</strong> Your wellness data helps us suggest activities and track your 
                progress toward your wellness goals.
              </li>
              <li>
                <strong>Privacy First:</strong> We never sell your personal data. Your information is encrypted 
                and stored securely.
              </li>
            </ul>
          </div>

          <div className="consent-section">
            <h2>Your Rights</h2>
            <ul>
              <li>You can review or update your consent preferences anytime in Settings → Privacy</li>
              <li>You can withdraw consent at any time</li>
              <li>You can request a copy of your data or request deletion</li>
            </ul>
          </div>

          <div className="consent-links">
            <a href="/Privacy-Policy" target="_blank" rel="noopener noreferrer">
              View Full Privacy Policy
            </a>
          </div>
        </div>

        {error && (
          <div className="consent-error" role="alert">
            {error}
          </div>
        )}

        <div className="consent-actions">
          <button
            type="button"
            onClick={handleAccept}
            disabled={accepting}
            className="consent-button primary"
          >
            {accepting ? 'Accepting...' : 'I Agree & Continue'}
          </button>
          <button
            type="button"
            onClick={handleLogout}
            className="consent-button secondary"
          >
            Log Out
          </button>
        </div>

        <div className="consent-footer">
          <p className="consent-note">
            By clicking "I Agree & Continue", you acknowledge that you have read and understood 
            the BloomPal Data & Wellness Consent and agree to the processing of your data as described.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ConsentPage;

